import React, { useEffect, useMemo, useState } from 'react';
import { SearchableDropdown, DatePicker, PdfUpload } from '@/components';
import {
  UseMitraOptionsResult,
  formatMitraLabel,
} from '@/hooks/useMitraOptions';
import { Provider } from '@/services/api/providers';
import { Pitstop } from '@/services/api/pitstops';
import { BUSINESS_UNIT_OPTIONS, getBusinessUnitLabel } from '@/types/common';

interface FormData {
  hubLocationID: string;
  vehicleHubID?: string;
  hubLocationName: string;
  businessUnit: string;
  vehicleID: string;
  driverID: number;
  driverName: string;
  driverCode: string;
  clientID: number;
  clientName: string;
  hubID: number;
  hubName: string;
  handoverDate: string;
  bastLink: string;
  notes: string;
  vehicleHandoverImageURL: string;
  depositMitraToDash?: number | string;
  unitEquipment: string[];
  sellingPrice?: number;
  modelSellingPrice?: number;
  priceType?: 'default' | 'custom';
}

interface ValidationErrors {
  [key: string]: string | undefined;
}

interface HandoverFormStep1Props {
  formData: FormData;
  errors: ValidationErrors;
  vehicles: any[];
  clients: Provider[];
  pitstops: Pitstop[];
  hubLocations: Pitstop[];
  loadingHubLocations?: boolean;
  loadingVehicles?: boolean;
  mitra: UseMitraOptionsResult;
  isEditMode?: boolean;
  lockBookingFields?: boolean;
  initialData?: any;
  hasActiveHandover?: boolean;
  isCheckingActiveHandover?: boolean;
  legacyBastLink?: string;
  onInputChange: (
    field: keyof FormData,
    value: string | string[] | number
  ) => void;
  onVehicleChange: (value: string) => void;
  onClientChange: (value: string) => void;
  onPitstopChange: (value: string) => void;
  onDriverChange: (value: string) => void;
  onHubLocationChange: (value: string) => void;
  onBusinessUnitChange: (value: string) => void;
}

const HandoverFormStep1: React.FC<HandoverFormStep1Props> = ({
  formData,
  errors,
  vehicles,
  clients,
  pitstops,
  hubLocations,
  loadingHubLocations = false,
  loadingVehicles = false,
  mitra,
  isEditMode = false,
  lockBookingFields = false,
  initialData,
  hasActiveHandover = false,
  isCheckingActiveHandover = false,
  legacyBastLink,
  onInputChange,
  onVehicleChange,
  onClientChange,
  onPitstopChange,
  onDriverChange,
  onHubLocationChange,
  onBusinessUnitChange,
}) => {
  // Get selected vehicle data
  const selectedVehicle = vehicles.find(
    (vehicle) => (vehicle.ID || vehicle.id).toString() === formData.vehicleID
  );
  const defaultPrice =
    selectedVehicle?.modelSellingPrice || formData.modelSellingPrice;
  const lockVehicleFields = (isEditMode && !!initialData) || lockBookingFields;
  const lockedMitraValue =
    formData.driverID && formData.driverID !== 0
      ? formatMitraLabel(
          formData.driverCode || initialData?.driverCode,
          formData.driverName || initialData?.driverName
        )
      : '-';

  // Typing in the mitra dropdown lifts state into the parent, re-rendering this
  // whole step — so the other lists are mapped once per data change, not per
  // keystroke.
  const hubLocationOptions = useMemo(
    () =>
      hubLocations.map((hub) => ({
        value: (hub.ID || hub.id).toString(),
        label: hub.name || '',
      })),
    [hubLocations]
  );

  const vehicleOptions = useMemo(
    () =>
      vehicles.map((vehicle) => ({
        value: vehicle.ID || vehicle.id,
        label: `${vehicle.platNomor} - ${vehicle.oemName} - ${vehicle.modelName}`,
        data: vehicle,
      })),
    [vehicles]
  );

  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: (client.ID || client.id).toString(),
        label: client.name,
        data: client,
      })),
    [clients]
  );

  const pitstopOptions = useMemo(
    () =>
      pitstops.map((pitstop) => {
        const cityName = pitstop.city?.name || pitstop.cityName || '';
        return {
          value: (pitstop.ID || pitstop.id).toString(),
          label: `${pitstop.name}${cityName ? ` (${cityName})` : ''}`,
          data: pitstop,
        };
      }),
    [pitstops]
  );

  // Set initial priceType in edit mode based on price comparison
  useEffect(() => {
    if (isEditMode && initialData && formData.modelSellingPrice !== undefined) {
      if (formData.modelSellingPrice === formData.sellingPrice) {
        onInputChange('priceType', 'default');
      } else {
        onInputChange('priceType', 'custom');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once when component mounts

  // IDR display state for formatted thousand-separator input
  const [idrDisplay, setIdrDisplay] = useState({
    sellingPrice: '',
    depositMitraToDash: '',
  });

  const formatIDR = (value: number | string): string => {
    const num =
      typeof value === 'string'
        ? parseFloat(String(value).replace(/\./g, '').replace(/,/g, '.'))
        : value;
    if (isNaN(num) || num === 0) return '';
    return num.toLocaleString('id-ID');
  };

  const handleIDRChange = (
    field: 'sellingPrice' | 'depositMitraToDash',
    raw: string
  ) => {
    const digitsOnly = raw.replace(/[^\d]/g, '');
    if (digitsOnly === '') {
      setIdrDisplay((prev) => ({ ...prev, [field]: '' }));
      onInputChange(field, '');
      return;
    }
    const numValue = parseInt(digitsOnly, 10);
    const formatted = numValue === 0 ? '0' : numValue.toLocaleString('id-ID');
    setIdrDisplay((prev) => ({ ...prev, [field]: formatted }));
    onInputChange(field, numValue);
  };

  // Sync idrDisplay when opening in edit mode
  useEffect(() => {
    if (isEditMode) {
      setIdrDisplay({
        sellingPrice: formatIDR(formData.sellingPrice ?? 0),
        depositMitraToDash: formatIDR(Number(formData.depositMitraToDash) ?? 0),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  return (
    <div className="">
      <div className="space-y-4">
        <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 border-b border-gray-200 heading-field">
          Vehicle Details
        </div>

        <div className="space-y-4">
          {/* Hub Location */}
          <div className="px-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hub Location <span className="text-red-500">*</span>
            </label>
            {lockVehicleFields ? (
              <input
                type="text"
                value={
                  formData.hubLocationName ||
                  initialData?.hubLocationName ||
                  initialData?.vehicleHubName ||
                  '-'
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none bg-gray-50 cursor-not-allowed"
                readOnly
                disabled
              />
            ) : (
              <SearchableDropdown
                placeholder={
                  loadingHubLocations
                    ? 'Loading hubs...'
                    : 'Search hub location or select...'
                }
                options={hubLocationOptions}
                value={formData.vehicleHubID || formData.hubLocationID}
                onChange={onHubLocationChange}
                disabled={loadingHubLocations}
                error={!!errors.vehicleHubID || !!errors.hubLocationID}
                className="w-full"
              />
            )}
            {(errors.vehicleHubID || errors.hubLocationID) && (
              <p className="text-red-500 text-xs mt-1">
                {errors.vehicleHubID || errors.hubLocationID}
              </p>
            )}
          </div>

          {/* Business Unit */}
          <div className="px-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Unit <span className="text-red-500">*</span>
            </label>
            {lockVehicleFields ? (
              <input
                type="text"
                value={getBusinessUnitLabel(
                  formData.businessUnit ||
                    initialData?.vehicleBusinessUnit ||
                    initialData?.businessUnit
                )}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none bg-gray-50 cursor-not-allowed"
                readOnly
                disabled
              />
            ) : (
              <SearchableDropdown
                placeholder="Select business unit"
                options={BUSINESS_UNIT_OPTIONS}
                value={formData.businessUnit}
                onChange={onBusinessUnitChange}
                error={!!errors.businessUnit}
                className="w-full"
              />
            )}
            {errors.businessUnit && (
              <p className="text-red-500 text-xs mt-1">{errors.businessUnit}</p>
            )}
          </div>

          {/* Plate Number */}
          <div className="px-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plate Number <span className="text-red-500">*</span>
            </label>
            {lockVehicleFields ? (
              <input
                type="text"
                value={(() => {
                  const plat =
                    initialData?.platNomor ||
                    initialData?.vehicle ||
                    selectedVehicle?.platNomor ||
                    '-';
                  const oem = initialData?.oemName || selectedVehicle?.oemName;
                  const model =
                    initialData?.modelName || selectedVehicle?.modelName;

                  if (plat !== '-' && oem && model) {
                    return `${plat} - ${oem} - ${model}`;
                  }
                  return plat;
                })()}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none bg-gray-50 cursor-not-allowed"
                readOnly
                disabled
              />
            ) : (
              <SearchableDropdown
                options={vehicleOptions}
                value={formData.vehicleID}
                onChange={onVehicleChange}
                placeholder={
                  !formData.hubLocationID || !formData.businessUnit
                    ? 'Select Hub Location and Business Unit first'
                    : loadingVehicles
                      ? 'Loading vehicles...'
                      : 'Search vehicles or select...'
                }
                emptyMessage="No vehicles found"
                error={!!errors.vehicleID}
                disabled={
                  !formData.hubLocationID ||
                  !formData.businessUnit ||
                  loadingVehicles
                }
                className="w-full"
              />
            )}
            {errors.vehicleID && (
              <p className="text-red-500 text-xs mt-1">{errors.vehicleID}</p>
            )}
          </div>
          <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 border-b border-gray-200 heading-field">
            Handover Details
          </div>

          {/* Clients */}
          <div className="px-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clients <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={clientOptions}
              value={formData.clientID.toString()}
              onChange={onClientChange}
              placeholder="Search clients or select..."
              emptyMessage="No clients found"
              error={!!errors.clientID}
              className="w-full"
            />
            {errors.clientID && (
              <p className="text-red-500 text-xs mt-1">{errors.clientID}</p>
            )}
          </div>

          {/* Pitstop */}
          <div className="px-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pitstop
            </label>
            <SearchableDropdown
              options={pitstopOptions}
              value={formData.hubID ? formData.hubID.toString() : ''}
              onChange={onPitstopChange}
              placeholder="Search pitstops or select..."
              emptyMessage="No pitstops found"
              error={!!errors.hubID}
              className="w-full"
            />
            {errors.hubID && (
              <p className="text-red-500 text-xs mt-1">{errors.hubID}</p>
            )}
          </div>

          {/* Mitra */}
          <div className="px-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mitra
            </label>
            {lockBookingFields || isEditMode ? (
              <input
                type="text"
                value={lockedMitraValue}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none bg-gray-50 cursor-not-allowed"
                readOnly
                disabled
              />
            ) : (
              <SearchableDropdown
                options={mitra.options}
                value={formData.driverID ? formData.driverID.toString() : ''}
                selectedLabelFallback={lockedMitraValue}
                onChange={onDriverChange}
                onSearchChange={mitra.setSearchTerm}
                onLoadMore={mitra.loadMore}
                hasMore={mitra.hasMore}
                loading={mitra.loading}
                disableLocalFilter
                placeholder="Search mitras or select..."
                emptyMessage="No mitras found"
                error={!!errors.driverID || hasActiveHandover}
                className="w-full"
              />
            )}
            {isCheckingActiveHandover && (
              <p className="text-gray-500 text-xs mt-1 flex items-center">
                <i className="ri-loader-4-line animate-spin mr-1"></i>
                Checking active handover...
              </p>
            )}
            {hasActiveHandover && !isCheckingActiveHandover && (
              <div className="flex items-start mt-1">
                <i className="ri-error-warning-line text-red-500 text-sm mr-1 mt-0"></i>
                <p className="text-red-500 text-xs">
                  Mitra ini sudah punya handover aktif
                </p>
              </div>
            )}
            {errors.driverID && (
              <p className="text-red-500 text-xs mt-1">{errors.driverID}</p>
            )}
          </div>

          {/* Tanggal Handover */}
          <div className="px-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tanggal & Waktu Handover <span className="text-red-500">*</span>
            </label>
            <DatePicker
              value={formData.handoverDate}
              onChange={(value: string) => onInputChange('handoverDate', value)}
              placeholder="Select handover date and time"
              showTimeSelect={true}
              error={!!errors.handoverDate}
              className="w-full"
            />
            {errors.handoverDate && (
              <p className="text-red-500 text-xs mt-1">{errors.handoverDate}</p>
            )}
          </div>

          {/* BAST Document (PDF) */}
          <div className="px-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document BAST <span className="text-red-500">*</span>
            </label>
            <PdfUpload
              value={formData.bastLink}
              legacyExternalUrl={legacyBastLink}
              onUploaded={(url) => onInputChange('bastLink', url)}
              onRemove={() => onInputChange('bastLink', '')}
              error={errors.bastLink}
            />
          </div>

          {/* Harga Jual Harian */}
          <div className="px-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Harga Jual Harian <span className="text-red-500">*</span>
            </label>

            {/* Radio Button Options */}
            <div className="flex items-center space-x-6 mb-3">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="harga-default"
                  name="priceType"
                  value="default"
                  checked={
                    formData.priceType === 'default' || !formData.priceType
                  }
                  onChange={() => {
                    onInputChange('priceType', 'default');
                    // Set to default value from selected vehicle
                    onInputChange('sellingPrice', defaultPrice);
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                />
                <label
                  htmlFor="harga-default"
                  className="ml-2 text-sm font-medium text-gray-700"
                >
                  Harga Default
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="radio"
                  id="harga-khusus"
                  name="priceType"
                  value="custom"
                  checked={formData.priceType === 'custom'}
                  onChange={() => {
                    onInputChange('priceType', 'custom');
                    // Reset to empty when custom is selected
                    onInputChange('sellingPrice', formData.sellingPrice || 0);
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                />
                <label
                  htmlFor="harga-khusus"
                  className="ml-2 text-sm font-medium text-gray-700"
                >
                  Harga Khusus
                </label>
              </div>
            </div>

            {/* Price Input Section */}
            {formData.priceType === 'default' || !formData.priceType ? (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Harga Default (Harian)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-sm text-gray-500">
                    Rp
                  </span>
                  <div className="w-full pl-8 pr-20 py-2 border border-gray-200 bg-gray-50 rounded-md text-sm cursor-not-allowed font-medium text-gray-800">
                    {formData.sellingPrice
                      ? formData.sellingPrice.toLocaleString('id-ID')
                      : defaultPrice.toLocaleString('id-ID')}
                  </div>
                  <div className="absolute inset-y-0 right-0 pr-3 pl-3 flex items-center pointer-events-none border-l border-gray-300">
                    <img
                      src="https://flagcdn.com/w40/id.png"
                      alt="IDR"
                      className="h-5 w-5 rounded-full object-cover mr-2 border border-gray-200"
                    />
                    <span className="text-sm text-gray-500">IDR</span>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Harga Khusus (Harian)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-sm text-gray-500">
                    Rp
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={idrDisplay.sellingPrice}
                    onChange={(e) =>
                      handleIDRChange('sellingPrice', e.target.value)
                    }
                    placeholder="60.000"
                    className="w-full pl-8 pr-24 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 pl-3 flex items-center pointer-events-none border-l border-gray-300">
                    <img
                      src="https://flagcdn.com/w40/id.png"
                      alt="IDR"
                      className="h-5 w-5 rounded-full object-cover mr-2 border border-gray-200"
                    />
                    <span className="text-sm text-gray-500">IDR</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deposit Mitra to Dash <span className="text-red-500">*</span>
              <span className="text-xs text-gray-500 font-normal">
                (One Time Payment)
              </span>{' '}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-sm text-gray-500">
                Rp
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={idrDisplay.depositMitraToDash}
                onChange={(e) =>
                  handleIDRChange('depositMitraToDash', e.target.value)
                }
                placeholder="0"
                className={`w-full pl-8 pr-24 py-2 border ${errors.depositMitraToDash ? 'border-red-500' : 'border-gray-300'} rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500`}
              />
              <div className="absolute inset-y-0 right-0 pr-3 pl-3 flex items-center pointer-events-none border-l border-gray-300">
                <img
                  src="https://flagcdn.com/w40/id.png"
                  alt="IDR"
                  className="h-5 w-5 rounded-full object-cover mr-2 border border-gray-200"
                />
                <span className="text-sm text-gray-500">IDR</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Input 0 jika tidak ada deposit, atau masukkan jumlah deposit.
            </p>
            {errors.depositMitraToDash && (
              <p className="text-red-500 text-xs mt-1">
                {errors.depositMitraToDash}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HandoverFormStep1;
